import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// This particular factory depends on filterType and selected operator.
import { defaultMemoize } from 'reselect';
import { compose } from 'recompose';
import cx from 'classnames';
import _map from 'lodash/map';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import _keyBy from 'lodash/keyBy';
import _isArray from 'lodash/isArray';
import _toNumber from 'lodash/toNumber';
import _head from 'lodash/head';
import _castArray from 'lodash/castArray';
// Constants
import { EMPTY_OBJECT, EMPTY_ARRAY, EMPTY_STRING } from '@tekion/tekion-base/app.constants';
import { getCurrencySymbol } from '@tekion/tekion-base/formatters/formatCurrency';
import { tget } from '@tekion/tekion-base/utils/general';
import Money from '@tekion/tekion-base/utils/money';
import { convertHoursToSec, convertSecToHours, toMoment } from '@tekion/tekion-base/utils/dateUtils';
import FILTER_TYPES from '../../../constants/filterSection.filterTypes';
import WORKSPACE_FILTER_TYPES from '../../../constants/filterSection.workspaceFilterTypes';
import { BETWEEN, BTW, BTW_INCLUSIVE } from '../../../constants/filterSection.operators';

// Components
import DateInput, { MonthPicker } from '../../../../../atoms/DateInput';
import VirtualizedSelect from '../../../../../molecules/virtualizedSelect';
import VirtualizedMultiSelect, { VirtualizedMultiAsync } from '../../../../../molecules/virtualizedMultiSelect';
import { Select, AdvanceAsyncSelect } from '../../../../../molecules/advancedSelect';
import DateTimeRangeWithoutPresets from '../../../molecules/DateTimeRange';
import DateRangeWithoutPresets from '../../../../../molecules/dateRangeWithoutPresets';
import NumberInput from '../../../../../molecules/NumberInput';
import DecimalInputRange from '../../../../../molecules/decimalInputRange';
import DecimalInput from '../../../../../molecules/decimalInput';
import Input from '../../../../../molecules/inputWrapper';
import { MultiSelect } from '../../../../../molecules/multiSelect';
import withDateAsFilter from '../../../containers/withDateAsFilter/withDateAsFilter';
import withSelectAllOptions from '../../../../../hoc/withSelectAllOptions';
import { getDatePicker } from '../../../utils/filterSection.utils';

// Helpers
import { getFilterType, getFilterAdditional } from '../../helpers/filterDialog.filterType';
import { getOptionsFromFilter, getFilterOptionFromFilter, getOverlayPlaceholder, getErrorFromFilter, getSortPropFromFilter, getShouldSortOptions, shouldShowSelectAll } from '../../../helpers/filterSection.filterType';

// styles
import styles from './withFilterValue.module.scss';
import NumberInputRange from '../../../../../molecules/numberInputRange';
var MultiSelectWithAll = withSelectAllOptions(MultiSelect);
var VALID_PROPS_TO_PASS = {
  SINGLE_SELECT: ['isDisabled', 'filterOption', 'sort', 'shouldSortOptions'],
  ASYNC_SINGLE_SELECT: ['filterOption', 'sort', 'filters', 'lookUpSearchApi', 'lookupByKeysApi', 'shouldFetchOnValueChange', 'shouldFetchInitialData', 'shouldHideOverlayIfNotVisible'],
  MULTI_SELECT: ['filterOption', 'error', 'isOptionDisabled', 'sort', 'shouldSortOptions', 'formatOptionLabel'],
  ASYNC_MULTI_SELECT: ['filterOption', 'sort', 'filters', 'lookUpSearchApi', 'lookupByKeysApi', 'isDisabled', 'shouldFetchOnValueChange', 'shouldFetchInitialData', 'shouldHideOverlayIfNotVisible', 'staticOptions'],
  DATE_WITHOUT_PRESET: ['disabledDate', 'getCalendarContainer', 'allowClear', 'disabled'],
  DATE_TIME_WITHOUT_PRESET: ['disabledDate', 'getCalendarContainer', 'allowClear', 'disabled', 'showTime', 'format'],
  DATE_PICKER: ['disabledDate'],
  MONTH_PICKER: ['disabledDate', 'allowClear', 'format'],
  NUMBER: ['parser', 'formatter', 'min'],
  WORKSPACE_ASYNC_MULTI_SELECT: ['filterOption', 'sort', 'filters', 'dealerIds', 'shouldFetchOnValueChange']
};
var SELECT_STYLE_OVERRIDES = {
  control: function control(_, _ref) {
    var isFocused = _ref.isFocused;
    if (!isFocused) {
      return {
        borderColor: 'transparent'
      };
    }
    return '';
  },
  menuPortal: function menuPortal(style) {
    return _objectSpread(_objectSpread({}, style), {}, {
      zIndex: 9999
    });
  }
};
var getSelectedOptionLabels = defaultMemoize(function (options, values) {
  var optionsMapByValue = _keyBy(options, 'value');
  return _map(values, function (selectedValue) {
    return _get(optionsMapByValue, "".concat(selectedValue, ".label"));
  });
});
var getSingleSelectProps = function getSingleSelectProps(filterType, value) {
  var options = getOptionsFromFilter(filterType);
  var filterOption = getFilterOptionFromFilter(filterType);
  var shouldSortOptions = getShouldSortOptions(filterType);
  var sort = getSortPropFromFilter(filterType);
  return {
    options: options,
    shouldSortOptions: shouldSortOptions,
    sort: sort,
    value: value.values,
    filterOption: filterOption
  };
};
var getMultiSelectProps = function getMultiSelectProps(filterType, value) {
  var options = getOptionsFromFilter(filterType);
  var overlayPlaceholder = getOverlayPlaceholder(filterType);
  var filterOption = getFilterOptionFromFilter(filterType);
  var error = getErrorFromFilter(filterType);
  var shouldSortOptions = getShouldSortOptions(filterType);
  var sort = getSortPropFromFilter(filterType);
  var baseProps = {
    options: options,
    shouldSortOptions: shouldSortOptions,
    sort: sort,
    dropDownClassName: styles.dropDownClassName,
    values: value.values,
    selectedValueLabels: getSelectedOptionLabels(options, value.values),
    filterOption: filterOption,
    overlayPlaceholder: overlayPlaceholder,
    error: error
  };
  return baseProps;
};
var getDateWithoutPresetProps = function getDateWithoutPresetProps(value, filterType) {
  var range = _get(value, 'values', EMPTY_ARRAY);
  return _objectSpread({
    values: range
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.DATE_WITHOUT_PRESET));
};
var getDatePickerProps = function getDatePickerProps(value) {
  return {
    value: toMoment(_head(_get(value, 'values')))
  };
};
var getAdvanceAsyncSelect = function getAdvanceAsyncSelect(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams;
  var baseProps = getSingleSelectProps(filterType, value);
  return {
    component: AdvanceAsyncSelect,
    baseProps: _objectSpread(_objectSpread(_objectSpread({}, baseProps), _pick(filterType.additional, VALID_PROPS_TO_PASS.ASYNC_SINGLE_SELECT)), {}, {
      resourceType: resourceType,
      resourceParams: resourceParams,
      key: key
    })
  };
};
var getSingleSelect = function getSingleSelect(filterType, value) {
  var baseProps = getSingleSelectProps(filterType, value);
  return {
    component: Select,
    baseProps: baseProps
  };
};
var getNumberProps = function getNumberProps(filterType, value) {
  return _objectSpread({
    value: value.values
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.NUMBER));
};
var getNumberInput = function getNumberInput(filterType, value) {
  var baseProps = getNumberProps(filterType, value);
  return {
    component: NumberInput,
    baseProps: baseProps
  };
};
var getMultiSelect = function getMultiSelect(filterType, value) {
  var baseProps = getMultiSelectProps(filterType, value);
  var showSelectAll = shouldShowSelectAll(filterType);
  return {
    component: showSelectAll ? MultiSelectWithAll : MultiSelect,
    baseProps: _objectSpread(_objectSpread({}, baseProps), _pick(filterType.additional, VALID_PROPS_TO_PASS.MULTI_SELECT))
  };
};
var getDateWithoutPreset = function getDateWithoutPreset(filterType, value) {
  var baseProps = getDateWithoutPresetProps(value, filterType);
  return {
    component: withDateAsFilter(DateRangeWithoutPresets),
    baseProps: baseProps
  };
};
var getDateTimeWithoutPresetProps = function getDateTimeWithoutPresetProps(value, filterType) {
  var range = _get(value, 'values', EMPTY_ARRAY);
  var containerClassName = tget(filterType, 'additional.containerClassName', EMPTY_STRING);
  return _objectSpread(_objectSpread({
    values: range
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.DATE_TIME_WITHOUT_PRESET)), {}, {
    containerClassName: cx(styles.dateTimePickerContainer, containerClassName),
    style: {
      width: '42rem'
    }
  });
};
var getDateTimeWithoutPreset = function getDateTimeWithoutPreset(filterType, value) {
  var baseProps = getDateTimeWithoutPresetProps(value, filterType);
  return {
    component: withDateAsFilter(DateTimeRangeWithoutPresets),
    baseProps: baseProps
  };
};
var getDatePickerDetails = function getDatePickerDetails(filterType, value) {
  var baseProps = getDatePickerProps(value, filterType);
  return {
    component: DateInput,
    baseProps: baseProps
  };
};
var getBasePropsForMonthPicker = function getBasePropsForMonthPicker() {
  var filterType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var values = arguments.length > 1 ? arguments[1] : undefined;
  var baseProps = _objectSpread({
    value: _head(values)
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.MONTH_PICKER));
  return baseProps;
};
var getMonthPicker = function getMonthPicker(filterType, values) {
  var _filterType$additiona;
  var baseProps = getBasePropsForMonthPicker(filterType, values);
  var MonthPickerComponent = ((_filterType$additiona = filterType.additional) === null || _filterType$additiona === void 0 ? void 0 : _filterType$additiona.component) || MonthPicker;
  return {
    baseProps: baseProps,
    component: MonthPickerComponent
  };
};
var getBasePropsForVirtualizedSelect = function getBasePropsForVirtualizedSelect(filterType, values) {
  var options = getOptionsFromFilter(filterType);
  return _objectSpread({
    options: options,
    dropDownClassName: styles.dropDownClassName,
    selectedValueLabels: getSelectedOptionLabels(options, values)
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.SINGLE_SELECT));
};
var getVirtualizedMultiSelectProps = function getVirtualizedMultiSelectProps(filterType, values) {
  var baseProps = getBasePropsForVirtualizedSelect(filterType, values);
  return {
    component: VirtualizedMultiSelect,
    baseProps: _objectSpread(_objectSpread({}, baseProps), {}, {
      values: values
    })
  };
};
var getVirtualizedSelectProps = function getVirtualizedSelectProps(filterType, values) {
  var baseProps = getBasePropsForVirtualizedSelect(filterType, values);
  return {
    component: VirtualizedSelect,
    baseProps: _objectSpread(_objectSpread({}, baseProps), {}, {
      value: values
    })
  };
};
var getMoneyProps = function getMoneyProps(filterType, value) {
  return {
    value: value.values,
    shouldDisabledStepper: true,
    addonBefore: getCurrencySymbol(),
    parser: Money.toInt,
    error: getErrorFromFilter(filterType),
    formatter: function formatter(valueToBeFormatted) {
      if (_isArray(valueToBeFormatted)) return EMPTY_STRING;
      return Money.withPrecision(valueToBeFormatted);
    }
  };
};
var getMoneyInput = function getMoneyInput(filterType, value) {
  var baseProps = getMoneyProps(filterType, value);
  return {
    component: NumberInput,
    baseProps: baseProps
  };
};
var getDecimalInputRangeProps = function getDecimalInputRangeProps(filterType, value) {
  return _objectSpread({
    values: value.values
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.NUMBER));
};
var getNumberInputRangeProps = function getNumberInputRangeProps(filterType, value) {
  return _objectSpread({
    values: value.values
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.NUMBER));
};
var getDecimalInputRange = function getDecimalInputRange(filterType, value) {
  var baseProps = getDecimalInputRangeProps(filterType, value);
  return {
    component: DecimalInputRange,
    baseProps: baseProps
  };
};
var getNumberInputRange = function getNumberInputRange(filterType, value) {
  var baseProps = getNumberInputRangeProps(filterType, value);
  return {
    component: NumberInputRange,
    baseProps: baseProps
  };
};
var getDecimalInputProps = function getDecimalInputProps(filterType, value) {
  return _objectSpread({
    value: compose(_head, _castArray)(value.values)
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.NUMBER));
};
var getDecimalInput = function getDecimalInput(filterType, value) {
  var baseProps = getDecimalInputProps(filterType, value);
  return {
    component: DecimalInput,
    baseProps: baseProps
  };
};
var getHourProps = function getHourProps(filterType, value) {
  return {
    value: value.values,
    parser: convertHoursToSec,
    formatter: function formatter(valueToBeFormatted) {
      return _isArray(valueToBeFormatted) ? EMPTY_STRING : _toNumber(convertSecToHours(valueToBeFormatted));
    },
    step: 3600,
    triggerChangeOnBlur: true
  };
};
var getHourInput = function getHourInput(filterType, value) {
  var baseProps = getHourProps(filterType, value);
  return {
    component: NumberInput,
    baseProps: baseProps
  };
};
var getStringInputProps = function getStringInputProps(filterType, value) {
  return {
    value: value.values,
    triggerChangeOnBlur: true
  };
};
var getStringInput = function getStringInput(filterType, value) {
  var baseProps = getStringInputProps(filterType, value);
  return {
    component: Input,
    baseProps: baseProps
  };
};
var advancedAsyncMultiSelectProps = function advancedAsyncMultiSelectProps(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams;
  var baseProps = _objectSpread({
    resourceType: resourceType,
    key: key,
    className: styles.select,
    value: value.values,
    styles: SELECT_STYLE_OVERRIDES,
    resourceParams: resourceParams,
    dropDownClassName: styles.dropDownClassName
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.ASYNC_MULTI_SELECT));
  return {
    component: VirtualizedMultiAsync,
    baseProps: baseProps
  };
};
var getCustomFilterProps = function getCustomFilterProps(filterType, value) {
  var _baseProps;
  var _ref2 = filterType || EMPTY_OBJECT,
    key = _ref2.key,
    additional = _ref2.additional;
  var _ref3 = additional || EMPTY_OBJECT,
    valueKey = _ref3.valueKey,
    className = _ref3.className,
    _ref3$containerClassN = _ref3.containerClassNameKey,
    containerClassNameKey = _ref3$containerClassN === void 0 ? 'className' : _ref3$containerClassN,
    stylesOverride = _ref3.stylesOverride,
    component = _ref3.component,
    dropDownClassName = _ref3.dropDownClassName;
  return {
    component: component,
    baseProps: (_baseProps = {
      additional: additional,
      key: key,
      styles: stylesOverride
    }, _defineProperty(_baseProps, valueKey, value.values), _defineProperty(_baseProps, containerClassNameKey, className), _defineProperty(_baseProps, "dropDownClassName", dropDownClassName), _defineProperty(_baseProps, "operator", value.operator), _baseProps)
  };
};

// Centralied
var workspaceAdvancedAsyncMultiSelectProps = function workspaceAdvancedAsyncMultiSelectProps(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams,
    FilterComponent = filterType.filterComponent;
  var baseProps = _objectSpread({
    resourceType: resourceType,
    key: key,
    className: styles.select,
    value: value.values,
    styles: SELECT_STYLE_OVERRIDES,
    resourceParams: resourceParams,
    dropDownClassName: styles.dropDownClassName
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.WORKSPACE_ASYNC_MULTI_SELECT));
  return {
    component: FilterComponent,
    baseProps: baseProps
  };
};
var getComponentForFilter = defaultMemoize(function () {
  var filterType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var typeOfFilter = getFilterType(filterType);
  var operator = value.operator,
    values = value.values;
  var additional = getFilterAdditional(value);
  if (typeOfFilter === FILTER_TYPES.CUSTOM) {
    return getCustomFilterProps(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.SINGLE_SELECT) {
    return getSingleSelect(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.ASYNC_SINGLE_SELECT) {
    return getAdvanceAsyncSelect(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.ASYNC_MULTI_SELECT) {
    return advancedAsyncMultiSelectProps(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.NUMBER && operator !== BETWEEN.id) {
    return getNumberInput(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.MULTI_SELECT) {
    return getMultiSelect(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_WITHOUT_PRESET) {
    return getDateWithoutPreset(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_TIME_WITHOUT_PRESET) {
    return getDateTimeWithoutPreset(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_PICKER) {
    return getDatePickerDetails(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.DATE) {
    return getDatePicker(filterType, values, additional);
  }
  if (typeOfFilter === FILTER_TYPES.MONTH_PICKER) {
    return getMonthPicker(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.VIRTUALIZED_MULTI_SELECT) {
    return getVirtualizedMultiSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.VIRTUALIZED_SELECT) {
    return getVirtualizedSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.MONEY_INPUT) {
    return getMoneyInput(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.AMOUNT_DOLLAR_RANGE && operator === BTW_INCLUSIVE.id) {
    return getDecimalInputRange(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.NUMBER_INPUT_RANGE && operator === BTW.id) {
    return getNumberInputRange(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.NUMBER_INPUT_WITH_RANGE) {
    // when operator is BTW then filter would have a range input else plain number iput
    return operator === BTW.id ? getNumberInputRange(filterType, value) : getNumberInput(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.AMOUNT_DOLLAR_RANGE) {
    return getDecimalInput(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.STRING) {
    return getStringInput(filterType, value);
  }
  if (typeOfFilter === FILTER_TYPES.HOUR_INPUT) {
    return getHourInput(filterType, value);
  }

  // Centralised
  if (typeOfFilter === WORKSPACE_FILTER_TYPES.WORKSPACE_ASYNC_MULTI_SELECT) {
    return workspaceAdvancedAsyncMultiSelectProps(filterType, value);
  }
  return getSingleSelect(filterType, value);
});
export default getComponentForFilter;